// Concierge Event form component
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { conciergeEventSchema, type ConciergeEventInput } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { createCrudApi } from '../../lib/api'
import type { ConciergePartner } from '../../../shared/types'

const partnersApi = createCrudApi<ConciergePartner, any>('concierge-partners')

interface Props {
  defaultValues?: Partial<ConciergeEventInput>
  onSubmit: (data: ConciergeEventInput) => void
  onCancel?: () => void
  loading?: boolean
}

export function ConciergeEventForm({ defaultValues, onSubmit, onCancel, loading }: Props) {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<ConciergeEventInput>({
    resolver: zodResolver(conciergeEventSchema),
    defaultValues: {
      visibility: 'public',
      featured: false,
      status: 'upcoming',
      ...defaultValues,
    },
  })

  // Fetch partners for dropdown
  const { data: partnersResponse } = useQuery({
    queryKey: ['concierge-partners'],
    queryFn: () => partnersApi.list({ limit: 100 }),
  })
  const partners = partnersResponse?.data || []

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Multi-language titles */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('concierge.sections.titles')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="titleEn" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.titleEn')} <span className="text-red-500">*</span>
            </label>
            <input
              {...register('titleEn')}
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.titleEn && (
              <p className="mt-1 text-sm text-red-600">{errors.titleEn.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="titleDe" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.titleDe')}
            </label>
            <input
              {...register('titleDe')}
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.titleDe && (
              <p className="mt-1 text-sm text-red-600">{errors.titleDe.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Multi-language descriptions */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('concierge.sections.descriptions')}</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="descriptionEn" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.descriptionEn')}
            </label>
            <textarea
              {...register('descriptionEn')}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.descriptionEn && (
              <p className="mt-1 text-sm text-red-600">{errors.descriptionEn.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="descriptionDe" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.descriptionDe')}
            </label>
            <textarea
              {...register('descriptionDe')}
              rows={4}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.descriptionDe && (
              <p className="mt-1 text-sm text-red-600">{errors.descriptionDe.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Event details */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('concierge.sections.details')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="partnerId" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.partner')}
            </label>
            <select
              {...register('partnerId')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="">{t('common.select')}</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
            {errors.partnerId && (
              <p className="mt-1 text-sm text-red-600">{errors.partnerId.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
              {t('fields.category')} <span className="text-red-500">*</span>
            </label>
            <select
              {...register('category')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="">{t('common.select')}</option>
              <option value="hospitality">{t('concierge.categories.hospitality')}</option>
              <option value="music">{t('concierge.categories.music')}</option>
              <option value="wine">{t('concierge.categories.wine')}</option>
              <option value="sport">{t('concierge.categories.sport')}</option>
              <option value="maritime">{t('concierge.categories.maritime')}</option>
              <option value="auto">{t('concierge.categories.auto')}</option>
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="visibility" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.visibility')}
            </label>
            <select
              {...register('visibility')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="public">{t('concierge.visibility.public')}</option>
              <option value="patron">{t('concierge.visibility.patron')}</option>
            </select>
            {errors.visibility && (
              <p className="mt-1 text-sm text-red-600">{errors.visibility.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              {t('fields.status')}
            </label>
            <select
              {...register('status')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            >
              <option value="upcoming">{t('concierge.status.upcoming')}</option>
              <option value="past">{t('concierge.status.past')}</option>
              <option value="cancelled">{t('concierge.status.cancelled')}</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
            )}
          </div>

          <div className="col-span-2">
            <label className="flex items-center gap-2">
              <input
                {...register('featured')}
                type="checkbox"
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-gray-700">{t('concierge.fields.featured')}</span>
            </label>
          </div>
        </div>
      </div>

      {/* Date and Location */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('concierge.sections.dateLocation')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.eventDate')} <span className="text-red-500">*</span>
            </label>
            <input
              {...register('eventDate')}
              type="datetime-local"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.eventDate && (
              <p className="mt-1 text-sm text-red-600">{errors.eventDate.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.location')} <span className="text-red-500">*</span>
            </label>
            <input
              {...register('location')}
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.location && (
              <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
            )}
          </div>

          <div className="col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.address')}
            </label>
            <input
              {...register('address')}
              type="text"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.address && (
              <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Capacity */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">{t('concierge.sections.capacity')}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.capacity')}
            </label>
            <input
              {...register('capacity')}
              type="text"
              placeholder="30"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.capacity && (
              <p className="mt-1 text-sm text-red-600">{errors.capacity.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="patronCapacity" className="block text-sm font-medium text-gray-700">
              {t('concierge.fields.patronCapacity')}
            </label>
            <input
              {...register('patronCapacity')}
              type="text"
              placeholder="10"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
            />
            {errors.patronCapacity && (
              <p className="mt-1 text-sm text-red-600">{errors.patronCapacity.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Image */}
      <div>
        <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700">
          {t('concierge.fields.imageUrl')}
        </label>
        <input
          {...register('imageUrl')}
          type="text"
          placeholder="/images/concierge/..."
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.imageUrl && (
          <p className="mt-1 text-sm text-red-600">{errors.imageUrl.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
